import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ErrorReason } from '~src/sessionManagement/SessionManagementService';

import { CreateIpAllowListRequest, ICreateIpAllowListResult } from './requests/CreateIpAllowListRequest';
import { DeleteIpAllowListRequest, IDeleteIpAllowListResult } from './requests/DeleteIpAllowListRequest';
import { GetIpAllowListRequest, IGetIpAllowListResult } from './requests/GetIpAllowListRequest';
import { IIpAllowListApiResult,IpAllowList } from './types/IpAllowList';

/**
 * IP Allowlist Management Service API
 * Singleton class for managing IP allowlist operations
 */
export class IpamsApi {
  private static s_instance: IpamsApi | null = null;
  private readonly MOCK_REQUESTS = false;
  private readonly ERROR_RATE = 0.2;
  private readonly MOCK_DELAY = 1000;

  /**
   * Get the singleton instance of IpamsApi
   * @returns The singleton instance
   */
  public static instance(): IpamsApi {
    if (!this.s_instance) {
      this.s_instance = new IpamsApi();
    }
    return this.s_instance;
  }

  /**
   * Helper function to check for errors in API responses
   * @param result The API response result
   * @returns The result if no errors, otherwise rejects with error code
   */
  private checkForErrors<T extends IIpAllowListApiResult>(result: T): T | Promise<never> {
    // Primary check: Look for explicit errors in the response
    if (result?.errors?.length) {
      return Promise.reject(result.errors[0].code);
    }

    // Secondary check: Validate that required fields exist and are not null/undefined
    if (result.requesterIp == null || result.siteLuid == null) {
      return Promise.reject(ErrorReason.Unknown);
    }

    return result;
  }

  /**
   * Get IP allowlist entries
   * @returns Promise resolving to IP allowlist data
   */
  public async getIpAllowlist(): Promise<IGetIpAllowListResult> {
    if (this.MOCK_REQUESTS) {
        return this.delayedMockResponse(this.createMockGetIpAllowlistResult());
    }

    const result = await WebHttpClient.sendRequest(new GetIpAllowListRequest());
    return this.checkForErrors(result);
  }

  /**
   * Create or save IP allowlist entries
   * @param allowedIPs Array of IP addresses to allow
   * @returns Promise resolving to created IP allowlist data
   */
  public async saveIpAllowlist(allowedIPs: string[]): Promise<ICreateIpAllowListResult> {
    if (this.MOCK_REQUESTS) {
        return this.delayedMockResponse(this.createMockSaveIpAllowlistResult(allowedIPs));
    }
    const result = await WebHttpClient.sendRequest(new CreateIpAllowListRequest({allowedIPs: allowedIPs}));
    return this.checkForErrors(result);
  }

  /**
   * Delete IP allowlist entries
   * @returns Promise resolving to deletion result
   */
  public async deleteIpAllowlist(): Promise<IDeleteIpAllowListResult> {
    if (this.MOCK_REQUESTS) {
        return this.delayedMockResponse(this.createMockDeleteIpAllowlistResult());
    }
    const result = await WebHttpClient.sendRequest(new DeleteIpAllowListRequest());
    return this.checkForErrors(result);
  }

  private delayedMockResponse<T>(result: T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      setTimeout(() => {
        if (Math.random() < this.ERROR_RATE) {
            // Simulate the 200 status with error payload case
            if (Math.random() < 0.5) {
              // Return error payload with 200 status
              reject(this.createMockErrorResult());
              return;
            }
            reject(new Error('Failed to perform API call'));
            return;
        }

        resolve(result);
      }, this.MOCK_DELAY);
    });
  }

  private createMockSaveIpAllowlistResult(allowedIPs: string[]): ICreateIpAllowListResult {
    return {
      ...this.createMockIpAllowlist(allowedIPs),
      requesterIp: '192.168.1.1',
    };
  }

  private createMockDeleteIpAllowlistResult(): IDeleteIpAllowListResult {
    return {
      ...this.createMockIpAllowlist([]),
      requesterIp: '192.168.1.1',
    };
  }

  private createMockGetIpAllowlistResult(): IGetIpAllowListResult {
    return {
      ...this.createMockIpAllowlist(['192.168.1.1', '10.0.0.0/24']),
      requesterIp: '192.168.1.1',
    };
  }

  private createMockIpAllowlist(allowedIPs: string[]): IpAllowList {
    return {
      allowedIPs: allowedIPs,
      updatedBy: 'test@test.com',
      updatedAt: new Date().toISOString(),
      siteLuid: '123',
      podId: '123',
      version: 1,
    };
  }

  /**
   * Creates a mock error response for testing error handling
   * @param errorCode The error code to return
   * @param errorMessage The error message to return
   * @returns Mock error result
   */
  private createMockErrorResult(errorCode: number = 17, errorMessage: string = 'Unable to create allowed ips for site'): ICreateIpAllowListResult {
    return {
      ...this.createMockIpAllowlist([]),
      requesterIp: '192.168.1.1',
      errors: [{
        code: errorCode,
        message: errorMessage
      }]
    };
  }
}
